import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faKey } from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/client";
import "./AccountPage.css";

const LANGUAGES = [
    { value: "en", label: "English" },
    { value: "fr", label: "Français" },
    { value: "zh", label: "中文 (繁體)" },
];

const AccountPage = () => {
    const { t } = useTranslation();
    const { user, updateUser } = useAuth();

    const [profile, setProfile] = useState({
        name: user?.name || "",
        company: user?.company || "",
        phone: user?.phone || "",
        website: user?.website || "",
        preferred_language: user?.preferred_language || "en",
    });
    const [profileMsg, setProfileMsg] = useState(null);
    const [savingProfile, setSavingProfile] = useState(false);

    const [pw, setPw] = useState({
        current_password: "",
        password: "",
        password_confirmation: "",
    });
    const [pwMsg, setPwMsg] = useState(null);
    const [savingPw, setSavingPw] = useState(false);

    const saveProfile = async (e) => {
        e.preventDefault();
        setSavingProfile(true);
        setProfileMsg(null);
        try {
            const { user: updated } = await api.updateProfile(profile);
            updateUser(updated);
            setProfileMsg({ ok: true, text: "Profile updated." });
        } catch (err) {
            setProfileMsg({ ok: false, text: err.message });
        } finally {
            setSavingProfile(false);
        }
    };

    const savePassword = async (e) => {
        e.preventDefault();
        setPwMsg(null);
        if (pw.password !== pw.password_confirmation) {
            setPwMsg({ ok: false, text: "New passwords do not match." });
            return;
        }
        if (pw.password.length < 10) {
            setPwMsg({
                ok: false,
                text: "New password must be at least 10 characters.",
            });
            return;
        }
        setSavingPw(true);
        try {
            await api.updatePassword(pw);
            setPw({
                current_password: "",
                password: "",
                password_confirmation: "",
            });
            setPwMsg({
                ok: true,
                text: "Password updated. Other devices have been signed out.",
            });
        } catch (err) {
            const detail =
                err.errors?.current_password?.[0] ||
                err.errors?.password?.[0] ||
                err.message;
            setPwMsg({ ok: false, text: detail });
        } finally {
            setSavingPw(false);
        }
    };

    return (
        <div className="account-page">
            <div className="section-header">
                <h2>My Account</h2>
            </div>

            <div className="account-page__grid">
                <form className="card account-page__card" onSubmit={saveProfile}>
                    <h3>
                        <FontAwesomeIcon icon={faUser} /> Profile
                    </h3>

                    <div className="form-group">
                        <label>Name</label>
                        <input
                            value={profile.name}
                            onChange={(e) =>
                                setProfile((p) => ({
                                    ...p,
                                    name: e.target.value,
                                }))
                            }
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>Email</label>
                        <input value={user?.email || ""} disabled />
                        <span className="account-page__hint">
                            Contact support to change your email address.
                        </span>
                    </div>
                    <div className="form-group">
                        <label>Company</label>
                        <input
                            value={profile.company}
                            onChange={(e) =>
                                setProfile((p) => ({
                                    ...p,
                                    company: e.target.value,
                                }))
                            }
                        />
                    </div>
                    <div className="form-group">
                        <label>Phone</label>
                        <input
                            value={profile.phone}
                            onChange={(e) =>
                                setProfile((p) => ({
                                    ...p,
                                    phone: e.target.value,
                                }))
                            }
                        />
                    </div>
                    <div className="form-group">
                        <label>Website</label>
                        <input
                            value={profile.website}
                            onChange={(e) =>
                                setProfile((p) => ({
                                    ...p,
                                    website: e.target.value,
                                }))
                            }
                            placeholder="https://…"
                        />
                    </div>
                    <div className="form-group">
                        <label>Preferred Language</label>
                        <select
                            value={profile.preferred_language}
                            onChange={(e) =>
                                setProfile((p) => ({
                                    ...p,
                                    preferred_language: e.target.value,
                                }))
                            }
                        >
                            {LANGUAGES.map((l) => (
                                <option key={l.value} value={l.value}>
                                    {l.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {profileMsg && (
                        <div
                            className={
                                profileMsg.ok
                                    ? "account-page__ok"
                                    : "error-message"
                            }
                        >
                            {profileMsg.text}
                        </div>
                    )}

                    <button
                        type="submit"
                        className="btn btn--primary btn--sm"
                        disabled={savingProfile}
                    >
                        {savingProfile ? "Saving…" : t("common.save")}
                    </button>
                </form>

                <form
                    className="card account-page__card"
                    onSubmit={savePassword}
                >
                    <h3>
                        <FontAwesomeIcon icon={faKey} /> Change Password
                    </h3>

                    <div className="form-group">
                        <label>Current Password</label>
                        <input
                            type="password"
                            value={pw.current_password}
                            onChange={(e) =>
                                setPw((p) => ({
                                    ...p,
                                    current_password: e.target.value,
                                }))
                            }
                            autoComplete="current-password"
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>New Password</label>
                        <input
                            type="password"
                            value={pw.password}
                            onChange={(e) =>
                                setPw((p) => ({
                                    ...p,
                                    password: e.target.value,
                                }))
                            }
                            autoComplete="new-password"
                            minLength={10}
                            required
                        />
                        <span className="account-page__hint">
                            At least 10 characters.
                        </span>
                    </div>
                    <div className="form-group">
                        <label>Confirm New Password</label>
                        <input
                            type="password"
                            value={pw.password_confirmation}
                            onChange={(e) =>
                                setPw((p) => ({
                                    ...p,
                                    password_confirmation: e.target.value,
                                }))
                            }
                            autoComplete="new-password"
                            required
                        />
                    </div>

                    {pwMsg && (
                        <div
                            className={
                                pwMsg.ok ? "account-page__ok" : "error-message"
                            }
                        >
                            {pwMsg.text}
                        </div>
                    )}

                    <button
                        type="submit"
                        className="btn btn--primary btn--sm"
                        disabled={savingPw}
                    >
                        {savingPw ? "Updating…" : "Update Password"}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default AccountPage;
